import { useEffect, useState } from "react";
import { encodeAbiParameters, keccak256 } from "viem";

import { useDeployment, useWallet } from "../../context";
import { useBlockNumber } from "../../hooks/useBlockNumber";
import { STATE_VIEW_ABI } from "../../lib/abis";
import { sortTokens, ZERO_ADDRESS } from "../../lib/constants";

import type { TokenSymbol } from "../../lib/constants";
import type { Address } from "viem";

interface V4PoolState {
  sqrtPriceX96: bigint;
  tick: number;
}

export function useV4PoolState(
  tokenA: TokenSymbol,
  tokenB: TokenSymbol,
  active: boolean,
) {
  const { deployment } = useDeployment();
  const { publicClient } = useWallet();
  const { data: blockNumber } = useBlockNumber();

  const [poolState, setPoolState] = useState<V4PoolState | null>(null);

  useEffect(() => {
    if (!active || !deployment?.v4.stateView || !deployment.tokens) return;

    let cancelled = false;
    async function fetchPoolState() {
      if (!deployment) return;
      const tokens = deployment.tokens as unknown as Record<string, Address>;
      const tokenAAddr = tokens[tokenA.toLowerCase()] as Address | undefined;
      const tokenBAddr = tokens[tokenB.toLowerCase()] as Address | undefined;
      if (!tokenAAddr || !tokenBAddr || tokenAAddr === tokenBAddr) return;

      const [currency0, currency1] = sortTokens(tokenAAddr, tokenBAddr);
      const poolId = keccak256(
        encodeAbiParameters(
          [
            { type: "address" },
            { type: "address" },
            { type: "uint24" },
            { type: "int24" },
            { type: "address" },
          ],
          [currency0, currency1, 3000, 60, ZERO_ADDRESS],
        ),
      );

      try {
        const result = (await publicClient.readContract({
          address: deployment.v4.stateView,
          abi: STATE_VIEW_ABI,
          functionName: "getSlot0",
          args: [poolId],
        })) as readonly [bigint, number, number, number];

        if (!cancelled) {
          const sqrtPriceX96 = result[0];
          const tick = result[1];
          if (sqrtPriceX96 > 0n) {
            setPoolState({ sqrtPriceX96, tick });
          } else {
            setPoolState(null);
          }
        }
      } catch {
        if (!cancelled) setPoolState(null);
      }
    }

    void fetchPoolState();
    return () => {
      cancelled = true;
    };
  }, [active, tokenA, tokenB, deployment, publicClient, blockNumber]);

  return poolState;
}
